import { Store } from '@ngrx/store';

import { WidgetUtils } from '../widgets/widget.utils';
import * as NgrxWidgetActions from '../store/actions/widget.actions';
import { TranslateService } from '@ngx-translate/core';
import {
  DocumentEditorActionsType,
  DocumentEditorNotification, DocumentEditorNotificationScope, DocumentEditorNotificationType,
  DocumentInfo,
  TemplateActionArgs,
  UiWidget
} from '@pt/pt-models';
import { GetChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse';
import { ChapterSelector } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterSelector';
import { TreatmentWarning } from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning';

export abstract class AbstractWidgetService {

  protected constructor(public store: Store,
                        public translateService: TranslateService) {
  }

  /**
   * Il trigger è formato da 3 parti
   *  - widgetId indica l'id del widget da modificare, l'argomento widget è il widget con id === widgetId
   *  - action è un enumerativo che spiega cosa fare
   *  - args è un insieme di parametri specifici per ogni widget che dicono cosa fare
   * @param context Informazioni di contesto tipo id Trattamento, tipo etc
   * @param widgets indica la lista di tutti i widget tra cui anche il target widget
   * @param widgetId indica l'id del widget target. Deve essere presente all'ìnterno di widgets
   * @param action  enumerativo di tutte le possibili azioni. Non tutti i widget le supportano tutte
   * @param args  argomento passato nel trigger
   * @param queryResponse
   */
  abstract execTrigger(context: GetChapterResponse, widgets: UiWidget[], widgetId: number, action: DocumentEditorActionsType, args: TemplateActionArgs, queryResponse?: any): UiWidget | null;

  execAsyncTrigger(sourceWidget: UiWidget, targetWidget: UiWidget, action: DocumentEditorActionsType, args: TemplateActionArgs, docInfo: DocumentInfo, targetChapterSelector: ChapterSelector, queryResponse?: any, resetWarning?: TreatmentWarning[]): void {
    this.unsupportedTrigger(sourceWidget);
  }

  /**
   * L'inizializzazione è formata da 3 parti
   *  - widgetId indica l'id del widget da modificare, l'argomento widget è il widget con id === widgetId
   *  - action è un enumerativo che spiega cosa fare
   *  - args è un insieme di parametri specifici per ogni widget che dicono cosa fare
   * @param context Informazioni di contesto tipo id Trattamento, tipo etc
   * @param widgets indica la lista di tutti i widget tra cui anche il target widget
   * @param widgetId indica l'id del widget target. Deve essere presente all'ìnterno di widgets
   * @param action  enumerativo di tutte le possibili azioni. Non tutti i widget le supportano tutte
   * @param args  argomento passato all'inizializzatore
   * @param queryResponse
   */
  abstract initialization(context: GetChapterResponse, widgets: UiWidget[], widgetId: number, action: DocumentEditorActionsType, args: TemplateActionArgs, queryResponse?: unknown): UiWidget | null;

  protected unsupportedTrigger(widget: UiWidget) {
    const data: DocumentEditorNotification = {
      id: WidgetUtils.generateId('unsupportedTrigger_'),
      name: widget.label,
      action: null,
      widgetInternalId: widget.internalId,
      type: DocumentEditorNotificationType.WARNING,
      message: this.translateService.stream('WIDGET_WARNING.UNSUPPORTED_TRIGGER'),
      timestamp: Date.now(),
      scope: DocumentEditorNotificationScope.TRIGGER,
      table: null,
      uuid: null
    };
    this.store.dispatch(NgrxWidgetActions.emitDocumentEditorNotification({ data }));
  }

  protected unsupportedInitialization(widget: UiWidget) {
    const data: DocumentEditorNotification = {
      id: WidgetUtils.generateId('unsupportedInitialization_'),
      name: widget.label,
      action: null,
      widgetInternalId: widget.internalId,
      type: DocumentEditorNotificationType.WARNING,
      message: this.translateService.stream('WIDGET_WARNING.UNSUPPORTED_INITIALIZATION'),
      timestamp: Date.now(),
      scope: DocumentEditorNotificationScope.INITIALIZATION,
      table: null,
      uuid: null
    };
    this.store.dispatch(NgrxWidgetActions.emitDocumentEditorNotification({ data }));
  }

  protected validWidgetAction(widget: UiWidget, action: DocumentEditorActionsType) {
    const data: DocumentEditorNotification = {
      id: WidgetUtils.generateId('validWidgetAction_'),
      name: widget.label,
      action,
      widgetInternalId: widget.internalId,
      type: DocumentEditorNotificationType.INFO,
      message: this.translateService.stream('WIDGET_WARNING.SUPPORTED_ACTION'),
      timestamp: Date.now(),
      scope: DocumentEditorNotificationScope.WIDGET_ACTION,
      table: null,
      uuid: null
    };
    this.store.dispatch(NgrxWidgetActions.emitDocumentEditorNotification({ data }));
  }

  protected invalidWidgetAction(widget: UiWidget, action: DocumentEditorActionsType) {
    const data: DocumentEditorNotification = {
      id: WidgetUtils.generateId('invalidWidgetAction_'),
      name: widget.label,
      action,
      widgetInternalId: widget.internalId,
      type: DocumentEditorNotificationType.WARNING,
      message: this.translateService.stream('WIDGET_WARNING.UNSUPPORTED_ACTION'),
      timestamp: Date.now(),
      scope: DocumentEditorNotificationScope.WIDGET_ACTION,
      table: null,
      uuid: null
    };
    this.store.dispatch(NgrxWidgetActions.emitDocumentEditorNotification({ data }));
  }

  protected invalidWidgetVersion(widget: UiWidget) {
    const data: DocumentEditorNotification = {
      id: WidgetUtils.generateId('invalidWidgetAction_'),
      name: widget.label,
      action: null,
      widgetInternalId: widget.internalId,
      type: DocumentEditorNotificationType.WARNING,
      message: this.translateService.stream('WIDGET_WARNING.UNSUPPORTED_VERSION'),
      timestamp: Date.now(),
      scope: DocumentEditorNotificationScope.WIDGET_ACTION,
      table: null,
      uuid: null
    };
    this.store.dispatch(NgrxWidgetActions.emitDocumentEditorNotification({ data }));
  }

  protected getCurrentValue(widget: UiWidget): any {
    return widget.value;
  }
}

